import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export interface CompanyMembership {
  id: string;
  user_id: string;
  company_id: string;
  role: "admin" | "manager" | "viewer";
  job_title: string | null;
  status: string;
  invited_by: string | null;
  joined_at: string | null;
  created_at: string;
  updated_at: string;
  company?: {
    id: string;
    name: string;
    slug: string | null;
    plan: string;
    status: string;
  };
}

export function useCompanyMemberships() {
  const { user } = useAuth();
  const [memberships, setMemberships] = useState<CompanyMembership[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchMemberships = useCallback(async () => {
    if (!user) {
      setMemberships([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    const { data, error } = await supabase
      .from("company_memberships")
      .select("*, companies(id, name, slug, plan, status)")
      .eq("user_id", user.id)
      .eq("status", "active")
      .order("joined_at", { ascending: true });

    if (!error && data) {
      const mapped = data.map((m: any) => ({
        ...m,
        company: m.companies || undefined,
      }));
      setMemberships(mapped);
    }

    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchMemberships();
  }, [fetchMemberships]);

  const switchCompany = async (companyId: string) => {
    if (!user) return;

    // Update profiles.company_id (active company pointer for RLS)
    const { error } = await supabase
      .from("profiles")
      .update({ company_id: companyId })
      .eq("user_id", user.id);

    if (error) {
      console.error("Error switching company:", error);
      throw error;
    }

    // Also sync user_roles for backward compatibility
    const membership = memberships.find((m) => m.company_id === companyId);
    if (membership) {
      await supabase
        .from("user_roles")
        .upsert(
          { user_id: user.id, role: membership.role },
          { onConflict: "user_id,role" }
        );

      // Delete other roles that don't match
      if (membership.role !== "admin") {
        await supabase.from("user_roles").delete().eq("user_id", user.id).neq("role", membership.role);
      }
    }

    // Store in localStorage
    localStorage.setItem(`active_company_${user.id}`, companyId);
  };

  const getStoredCompanyId = (): string | null => {
    if (!user) return null;
    return localStorage.getItem(`active_company_${user.id}`);
  };

  return {
    memberships,
    loading,
    switchCompany,
    getStoredCompanyId,
    refetch: fetchMemberships,
    hasMultipleCompanies: memberships.length > 1,
  };
}
